import React, { useState, useEffect, useCallback } from 'react';

// Definición de roles disponibles para generar HUs
const AVAILABLE_ROLES = [
  { id: 'back', tag: '[Back]', name: 'Backend', icon: '⚙️' },
  { id: 'front', tag: '[Front]', name: 'Frontend', icon: '🎨' },
  { id: 'integracion', tag: '[Integración]', name: 'Integración', icon: '🔗' },
  { id: 'funcional', tag: '[Funcional]', name: 'Funcional', icon: '🧩' },
  { id: 'qa', tag: '[QA]', name: 'QA Funcional', icon: '✅' },
  { id: 'qa-auto', tag: '[QA-Auto]', name: 'QA Auto', icon: '🤖' },
  { id: 'qa-nf', tag: '[QA-NF]', name: 'QA NF', icon: '📊' },
  { id: 'arquitectura', tag: '[Arquitectura]', name: 'Arquitectura', icon: '🏛️' },
  { id: 'data', tag: '[Data]', name: 'Datos', icon: '📈' },
  { id: 'ux', tag: '[UX]', name: 'UX/UI', icon: '✏️' },
  { id: 'devops', tag: '[DevOps]', name: 'DevOps', icon: '🚀' },
  { id: 'seguridad', tag: '[Seguridad]', name: 'Seguridad', icon: '🔒' },
  { id: 'mobile', tag: '[Mobile]', name: 'Mobile', icon: '📱' },
  { id: 'docs', tag: '[Docs]', name: 'Docs', icon: '📝' },
];

// Plantillas predefinidas
const PRESET_TEMPLATES = [
  { id: 'dev-full', name: 'Dev Completo', icon: '🔧', roles: ['back', 'front', 'integracion', 'qa'] },
  { id: 'functional', name: 'Funcional', icon: '🧩', roles: ['funcional', 'qa'] },
  { id: 'backend', name: 'Backend', icon: '⚙️', roles: ['back'] },
  { id: 'frontend', name: 'Frontend', icon: '🎨', roles: ['front'] },
  { id: 'quality', name: 'QA', icon: '✅', roles: ['qa', 'qa-auto', 'qa-nf'] },
  { id: 'full-team', name: 'Equipo', icon: '👥', roles: ['back', 'front', 'integracion', 'funcional', 'qa', 'arquitectura', 'devops', 'ux'] },
  { id: 'data', name: 'Datos', icon: '📊', roles: ['data', 'back', 'devops'] },
  { id: 'custom', name: 'Custom', icon: '⚡', roles: [] },
];

interface InstructionsBuilderProps {
  onInstructionsChange: (instructions: string) => void;
  initialInstructions?: string;
  onRolesChange?: (roles: Array<{ id: string; tag: string; name: string }>) => void;
}

const InstructionsBuilder: React.FC<InstructionsBuilderProps> = ({
  onInstructionsChange,
  onRolesChange,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['back', 'front', 'integracion']);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('dev-full');
  const [epicId, setEpicId] = useState<string>('');
  const [initiative, setInitiative] = useState<string>('');
  const [prefix, setPrefix] = useState<string>('GFT');
  const [storyIdPrefix, setStoryIdPrefix] = useState<string>('');
  const [freeText, setFreeText] = useState<string>('');
  const [useNomenclature, setUseNomenclature] = useState<boolean>(true);
  const [showRoles, setShowRoles] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const buildInstructions = useCallback((): string => {
    const parts: string[] = [];

    // Roles seleccionados
    if (selectedRoles.length > 0) {
      const roleNames = selectedRoles
        .map(id => AVAILABLE_ROLES.find(r => r.id === id)?.name)
        .filter(Boolean)
        .join(', ');
      parts.push(`📋 ROLES A GENERAR: ${roleNames}`);
      
      const tags = selectedRoles
        .map(id => AVAILABLE_ROLES.find(r => r.id === id)?.tag)
        .filter(Boolean)
        .join(', ');
      parts.push(`   Tags: ${tags}`);

      if (selectedRoles.includes('funcional')) {
        parts.push('   Enfoque funcional: priorizar reglas de negocio textuales y contexto funcional sobre detalle técnico');
      }
    }

    // Nomenclatura
    if (useNomenclature) {
      parts.push(`\n🏷️ ESTÁNDAR DE NOMENCLATURA:`);
      const idPart = storyIdPrefix || '[ID]';
      parts.push(`   Formato: ${idPart}-${prefix}-${initiative || '[Iniciativa]'}-[Tag] Título`);
      if (storyIdPrefix) {
        parts.push(`   ID/Prefijo de HU: ${storyIdPrefix}`);
      }
      if (initiative) {
        parts.push(`   Iniciativa: ${initiative}`);
      }
      if (prefix !== 'GFT') {
        parts.push(`   Organización: ${prefix}`);
      }
    }

    // Epic ID
    if (epicId) {
      parts.push(`\n📌 ÉPICA ID: ${epicId}`);
    }

    // Texto libre
    if (freeText.trim()) {
      parts.push(`\n📝 INSTRUCCIONES ADICIONALES:\n${freeText}`);
    }

    return parts.join('\n');
  }, [selectedRoles, epicId, initiative, prefix, storyIdPrefix, freeText, useNomenclature]);

  useEffect(() => {
    const instructions = buildInstructions();
    onInstructionsChange(instructions);
  }, [buildInstructions, onInstructionsChange]);

  useEffect(() => {
    if (!onRolesChange) return;
    const roles = selectedRoles
      .map(id => AVAILABLE_ROLES.find(r => r.id === id))
      .filter(Boolean) as Array<{ id: string; tag: string; name: string }>;
    onRolesChange(roles);
  }, [selectedRoles, onRolesChange]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = PRESET_TEMPLATES.find(t => t.id === templateId);
    if (template && template.id !== 'custom') {
      setSelectedRoles(template.roles);
    }
  };

  const toggleRole = (roleId: string) => {
    setSelectedTemplate('custom');
    setSelectedRoles(prev => 
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4">
      <h3 className="text-lg font-bold text-gft-dark mb-3 flex items-center">
        <span className="mr-2">📝</span>
        Constructor de Instrucciones
      </h3>

      {/* Plantillas */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
          🎯 Plantilla
        </label>
        <div className="flex flex-wrap gap-1">
          {PRESET_TEMPLATES.map(template => (
            <button
              key={template.id}
              onClick={() => handleTemplateChange(template.id)}
              title={template.name}
              className={`px-2 py-1 rounded text-xs transition-all flex items-center gap-1 ${
                selectedTemplate === template.id
                  ? 'bg-gft-purple text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <span>{template.icon}</span>
              <span className="hidden sm:inline">{template.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Roles - Expandible */}
      <div className="mb-3">
        <button
          onClick={() => setShowRoles(!showRoles)}
          className="w-full flex justify-between items-center text-xs font-semibold text-gray-700 p-2 bg-gray-50 rounded hover:bg-gray-100"
        >
          <span>👥 Roles: {selectedRoles.length} seleccionados</span>
          <span className="text-gray-400">{showRoles ? '▲' : '▼'}</span>
        </button>
        
        {showRoles && (
          <div className="mt-1.5 p-2 bg-gray-50 rounded border">
            <div className="flex justify-end gap-2 mb-1.5 text-xs">
              <button 
                onClick={() => { setSelectedTemplate('custom'); setSelectedRoles(AVAILABLE_ROLES.map(r => r.id)); }}
                className="text-gft-purple hover:underline"
              >
                Todos
              </button>
              <span className="text-gray-300">|</span>
              <button 
                onClick={() => { setSelectedTemplate('custom'); setSelectedRoles([]); }}
                className="text-gray-500 hover:underline"
              >
                Ninguno
              </button>
            </div>
            
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-1">
              {AVAILABLE_ROLES.map(role => (
                <button
                  key={role.id}
                  onClick={() => toggleRole(role.id)}
                  title={`${role.name} ${role.tag}`}
                  className={`p-1.5 rounded text-center transition-all ${
                    selectedRoles.includes(role.id)
                      ? 'bg-purple-200 text-gft-purple ring-1 ring-gft-purple'
                      : 'bg-white hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <div className="text-sm">{role.icon}</div>
                  <div className="text-[10px] leading-tight truncate">{role.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Nomenclatura */}
      <div className="mb-3 p-2 bg-gray-50 rounded">
        <label className="flex items-center cursor-pointer mb-2">
          <input
            type="checkbox"
            checked={useNomenclature}
            onChange={(e) => setUseNomenclature(e.target.checked)}
            className="w-3.5 h-3.5 text-gft-purple rounded mr-1.5"
          />
          <span className="text-xs font-semibold text-gray-700">
            🏷️ Nomenclatura
          </span>
        </label>
        
        {useNomenclature && (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-1.5">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                  ID/Prefijo HU
                </label>
                <input
                  type="text"
                  value={storyIdPrefix}
                  onChange={(e) => setStoryIdPrefix(e.target.value)}
                  placeholder="001, HU01"
                  className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-gft-purple focus:border-gft-purple"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                  Org.
                </label>
                <input
                  type="text"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="GFT"
                  className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-gft-purple focus:border-gft-purple"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">
                  Iniciativa
                </label>
                <input
                  type="text"
                  value={initiative}
                  onChange={(e) => setInitiative(e.target.value)}
                  placeholder="Portal"
                  className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-gft-purple focus:border-gft-purple"
                />
              </div>
            </div>
            
            <div className="text-[10px] bg-white px-2 py-1 rounded border text-gray-600 overflow-hidden">
              <span className="font-medium">Formato: </span>
              <code className="text-gft-purple">
                {storyIdPrefix || '[ID]'}-{prefix}-{initiative || '[Init]'}-[Tag] Título
              </code>
            </div>
          </div>
        )}
      </div>

      {/* Epic ID */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          📌 Épica ID
        </label>
        <input
          type="text"
          value={epicId}
          onChange={(e) => setEpicId(e.target.value)}
          placeholder="12345, EPIC-001 (opcional)"
          className="w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-gft-purple focus:border-gft-purple"
        />
      </div>

      {/* Texto Libre */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          ✍️ Instrucciones Extra
        </label>
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="Especificaciones adicionales..."
          rows={2}
          className="w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-gft-purple focus:border-gft-purple resize-none"
        />
      </div>

      {/* Preview Toggle */}
      <div className="bg-purple-50 rounded overflow-hidden">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="w-full px-2 py-1.5 flex justify-between items-center text-xs font-medium text-gft-purple hover:bg-purple-100"
        >
          <span>👁️ Vista Previa</span>
          <span className="text-purple-400">{showPreview ? '▲' : '▼'}</span>
        </button>
        
        {showPreview && (
          <pre className="text-[10px] text-gray-600 whitespace-pre-wrap bg-white m-1.5 p-2 rounded border max-h-24 overflow-auto">
            {buildInstructions() || '(Sin instrucciones)'}
          </pre>
        )}
      </div>
    </div>
  );
};

export default InstructionsBuilder;
